import * as React from 'react'

import { useVehicleForm } from '../../context/VehicleFormContext'
import VehicleBasicForm from '../../forms/vehicle/VehicleBasicForm'
import AddVehicleFooter from './AddVehicleFooter'
import AddVehicleHeader from './AddVehicleHeader'
import useVehicleService from '../../services/VehicleService'
import { toast } from '@/lib/toasts/api'

import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'

const extractCreatedVehicleId = (res: any): number | null => {
  const raw =
    res?.data?.id ?? res?.data?.vehicleId?.value ?? res?.data?.vehicleId
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

const AddVehicleModal = ({
  isOpen,
  close,
  companyId,
  refreshVehicle,
  onCreated,
}: {
  isOpen: boolean
  close: () => void
  companyId: string
  refreshVehicle?: () => void
  onCreated?: (vehicleId: number) => void
}) => {
  const { addVehicle } = useVehicleService()
  const loading = useLoadingStore(selectLoading)
  const { refresh } = useVehicles()

  const { t } = useLocalize()
  const { data, isActive, setIsReadyToSubmit, isEditState, setCompanyIdState } =
    useVehicleForm()

  const modalRef: any = React.useRef(null)
  const [submitted, setSubmitted] = React.useState(false)

  const validateRequiredFields = (
    form: HTMLFormElement | null,
    showErrors = false,
  ): boolean => {
    if (!form) return false

    const requiredInputs = form.querySelectorAll('[required]')
    let allValid = true

    requiredInputs.forEach((input: HTMLInputElement) => {
      if (input.type === 'hidden') {
        if (!input.value || input.value.trim() === '') {
          allValid = false
        }
      } else if (!input.value || input.value.trim() === '') {
        allValid = false
        if (showErrors) {
          input.setCustomValidity(t('common.required-field'))
        }
      } else {
        input.setCustomValidity('')
      }
    })

    if (showErrors && !allValid) {
      form.reportValidity()
    }

    return allValid
  }

  const addVehicleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitted(true)

    if (!modalRef.current) return
    const form = modalRef.current as HTMLFormElement

    const allValid = validateRequiredFields(form, true)
    setIsReadyToSubmit(allValid)

    if (!allValid) {
      toast.error({ text: t('vehicleModule.missing-required-fields') })
      return
    }

    const formattedData = {
      ...data,
      id: data?.id || '0',
      isActive,
    }

    try {
      const res = await addVehicle({ companyId, vehicle: formattedData })
      toast.success({ text: t('vehicleModule.saved-successfully') })
      const newId = extractCreatedVehicleId(res)
      if (newId != null) onCreated?.(newId)
      close()
      refresh()
      refreshVehicle?.()
    } catch (err) {
      toast.error({
        text: isEditState
          ? t('vehicleModule.error-saving-changes')
          : t('vehicleModule.error-adding-vehicle'),
      })
    }
  }

  React.useEffect(() => {
    if (!isOpen) return
    setCompanyIdState(companyId)
  }, [isOpen, companyId])

  // `return null` musi stać POD hookami - inaczej przy zmianie `isOpen` React widzi
  // inną liczbę hooków niż w poprzednim renderze.
  if (!isOpen) return null

  return (
    <ModalLayout>
      <form
        onSubmit={(e) => addVehicleSubmit(e)}
        noValidate
        ref={modalRef}
        className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[720px] tw-flex-col tw-rounded-lg tw-bg-white"
      >
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        <AddVehicleHeader close={close} />
        <div className="tw-flex-1 tw-overflow-y-auto">
          <VehicleBasicForm />
        </div>
        <AddVehicleFooter />
      </form>
    </ModalLayout>
  )
}

export default AddVehicleModal
